/**
 * useMarketplaceDocuments Hook
 *
 * Manages document display and upload for marketplace/collaboration pages.
 * Same pattern as useDocumentUpload hook in lead wizard, but reads from:
 * - lead_data JSON (master values for resolving document definitions)
 * - marketplace_lead_documents (existing uploaded docs)
 * - collaborationService (upload/delete API)
 *
 * Usage:
 *   const { resolvedDocs, groupedDocs, uploadedDocs, ... } = useMarketplaceDocuments({
 *     leadData,           // lead_data JSON from marketplace lead
 *     collaborationId,    // collaboration ID for upload/delete
 *     documents,          // existing marketplace_lead_documents array
 *     canUpload,          // whether user can upload
 *     toast,              // toast ref
 *   });
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { resolveDocuments } from '../services/offerCalculationService';
import collaborationService from '../services/collaborationService';

const useMarketplaceDocuments = ({ leadData, collaborationId, documents = [], canUpload = false, toast }) => {
    const [resolvedDocs, setResolvedDocs] = useState([]);
    const [loadingDocMapping, setLoadingDocMapping] = useState(false);
    const [uploadingDocs, setUploadingDocs] = useState({});
    const [uploadedDocs, setUploadedDocs] = useState({});
    const [uploadProgress, setUploadProgress] = useState({});

    // Extract master values from lead_data JSON
    const masterValues = useMemo(() => {
        if (!leadData) return null;

        // Flatten all stages to find master values
        const flat = {};
        const stages = ['lead_capture', 'entry_level', 'lead_details', 'offer_generation', 'eligibility'];
        stages.forEach(stage => {
            if (leadData[stage] && typeof leadData[stage] === 'object') {
                Object.assign(flat, leadData[stage]);
            }
        });

        const values = {};
        if (flat._master_applicant_type?.id) values.applicant_type = [flat._master_applicant_type.id];
        if (flat._master_employment_status?.id) values.employment_status = [flat._master_employment_status.id];
        if (flat._master_employment_sub_status?.id) values.employment_sub_status = [flat._master_employment_sub_status.id];
        if (flat._master_income_type?.id) values.income_type = [flat._master_income_type.id];

        return Object.keys(values).length > 0 ? values : null;
    }, [leadData]);

    // Resolve document definitions from master values
    useEffect(() => {
        if (!masterValues) {
            setResolvedDocs([]);
            return;
        }

        const loadDocuments = async () => {
            setLoadingDocMapping(true);
            try {
                const response = await resolveDocuments(masterValues);
                if (response.success && response.data) {
                    setResolvedDocs(response.data);
                } else {
                    setResolvedDocs([]);
                }
            } catch (error) {
                console.error('Error resolving documents:', error);
                setResolvedDocs([]);
            } finally {
                setLoadingDocMapping(false);
            }
        };
        loadDocuments();
    }, [masterValues]);

    // Group existing marketplace documents by document_definition_id
    useEffect(() => {
        if (!documents || documents.length === 0) {
            setUploadedDocs({});
            return;
        }

        const grouped = {};
        documents.forEach(doc => {
            const defId = doc.document_definition_id;
            if (!defId) return;

            if (!grouped[defId]) {
                grouped[defId] = [];
            }
            // Map to same format as lead_document_uploads for consistency
            grouped[defId].push({
                id: doc.id,
                original_filename: doc.file_name,
                file_size: doc.file_size,
                mime_type: doc.mime_type,
                status: doc.is_verified ? 'verified' : 'uploaded',
                url: doc.file_url || doc.file_path,
                source: doc.source,
                document_definition_id: doc.document_definition_id,
                document_definition: doc.document_definition,
                uploader: doc.uploader,
            });
        });
        setUploadedDocs(grouped);
    }, [documents]);

    // Group resolved documents by document_group_id
    const groupedDocs = useMemo(() => {
        const groups = {};
        const ungrouped = [];
        resolvedDocs.forEach((doc) => {
            if (doc.document_group_id) {
                if (!groups[doc.document_group_id]) {
                    groups[doc.document_group_id] = {
                        id: doc.document_group_id,
                        name: doc.document_group_name || 'Document Group',
                        docs: [],
                    };
                }
                groups[doc.document_group_id].docs.push(doc);
            } else {
                ungrouped.push(doc);
            }
        });
        return { groups: Object.values(groups), ungrouped };
    }, [resolvedDocs]);

    // Get uploaded files for a document definition
    const getUploadedFilesForDefinition = useCallback((docDefId) => {
        return uploadedDocs[docDefId] || [];
    }, [uploadedDocs]);

    // Handle document upload
    const handleDocumentSelect = useCallback(async (file, documentDefinitionId) => {
        if (!collaborationId || !canUpload) return;

        setUploadingDocs(prev => ({ ...prev, [documentDefinitionId]: true }));
        setUploadProgress(prev => ({ ...prev, [documentDefinitionId]: 0 }));

        try {
            const result = await collaborationService.uploadDocument(
                collaborationId,
                file,
                { document_definition_id: documentDefinitionId }
            );

            if (result.success) {
                const newDoc = result.data;
                setUploadedDocs(prev => ({
                    ...prev,
                    [documentDefinitionId]: [
                        ...(prev[documentDefinitionId] || []),
                        {
                            id: newDoc.id,
                            original_filename: newDoc.file_name,
                            file_size: newDoc.file_size,
                            mime_type: newDoc.mime_type,
                            status: 'uploaded',
                            url: newDoc.file_url || newDoc.file_path,
                            source: 'collaboration',
                            document_definition_id: documentDefinitionId,
                            document_definition: newDoc.document_definition,
                        }
                    ],
                }));
                toast?.current?.show({ severity: 'success', summary: 'Uploaded', detail: 'Document uploaded successfully', life: 3000 });
            } else {
                toast?.current?.show({ severity: 'error', summary: 'Upload Failed', detail: result.message || 'Failed to upload document', life: 4000 });
            }
        } catch (error) {
            console.error('Document upload error:', error);
            toast?.current?.show({ severity: 'error', summary: 'Upload Failed', detail: error.response?.data?.message || 'Failed to upload document', life: 4000 });
        } finally {
            setUploadingDocs(prev => ({ ...prev, [documentDefinitionId]: false }));
            setUploadProgress(prev => ({ ...prev, [documentDefinitionId]: 0 }));
        }
    }, [collaborationId, canUpload, toast]);

    // Handle document removal
    const handleDocumentRemove = useCallback(async (uploadId, documentDefinitionId) => {
        if (!collaborationId) return;

        try {
            const result = await collaborationService.deleteDocument(collaborationId, uploadId);
            if (result.success) {
                setUploadedDocs(prev => {
                    const updated = { ...prev };
                    if (updated[documentDefinitionId]) {
                        updated[documentDefinitionId] = updated[documentDefinitionId].filter(f => f.id !== uploadId);
                        if (updated[documentDefinitionId].length === 0) {
                            delete updated[documentDefinitionId];
                        }
                    }
                    return updated;
                });
                toast?.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Document removed', life: 3000 });
            } else {
                toast?.current?.show({ severity: 'error', summary: 'Error', detail: result.message || 'Failed to delete document', life: 3000 });
            }
        } catch (error) {
            console.error('Document delete error:', error);
            toast?.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete document', life: 3000 });
        }
    }, [collaborationId, toast]);

    return {
        resolvedDocs,
        groupedDocs,
        loadingDocMapping,
        uploadingDocs,
        uploadedDocs,
        uploadProgress,
        getUploadedFilesForDefinition,
        handleDocumentSelect,
        handleDocumentRemove,
        masterValues,
    };
};

export default useMarketplaceDocuments;
